import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Clock, Menu, MessageSquare, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  fetchTemplatesByTrack,
  saveTrack,
  MESSAGE_TYPES,
  MESSAGE_TYPE_LABELS,
  MRT_VALUES,
  MRT_LABELS
} from '../../services/appointmentMessageVariantTemplates.service';

const AppointmentMessageVariantsPage = () => {
  const { currentUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTrack, setActiveTrack] = useState(1);
  
  // Templates organized by track
  const [templatesByTrack, setTemplatesByTrack] = useState({
    1: {},
    2: {},
    3: {},
    4: {}
  });

  // Form state for current track
  const [formData, setFormData] = useState({
    [MESSAGE_TYPES.CONFIRMATION]: '',
    [MESSAGE_TYPES.REMINDER_24HR]: '',
    [MESSAGE_TYPES.REMINDER_1HR]: ''
  });

  // MRT state for each track
  const [mrtByTrack, setMrtByTrack] = useState({
    1: '',
    2: '',
    3: '',
    4: ''
  });

  // Response state for each track and message type
  const [responsesByTrack, setResponsesByTrack] = useState({
    1: {},
    2: {},
    3: {},
    4: {}
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    // Update form data when active track changes
    const trackTemplates = templatesByTrack[activeTrack] || {};
    setFormData({
      [MESSAGE_TYPES.CONFIRMATION]: trackTemplates[MESSAGE_TYPES.CONFIRMATION]?.content || '',
      [MESSAGE_TYPES.REMINDER_24HR]: trackTemplates[MESSAGE_TYPES.REMINDER_24HR]?.content || '',
      [MESSAGE_TYPES.REMINDER_1HR]: trackTemplates[MESSAGE_TYPES.REMINDER_1HR]?.content || ''
    });
  }, [activeTrack, templatesByTrack]);

  useEffect(() => {
    // Load MRT values from templates when data is fetched
    const newMrtByTrack = { 1: '', 2: '', 3: '', 4: '' };
    [1, 2, 3, 4].forEach(track => {
      const trackTemplates = templatesByTrack[track] || {};
      // Get MRT from first available template in the track
      const firstTemplate = Object.values(trackTemplates)[0];
      if (firstTemplate?.mrt) {
        newMrtByTrack[track] = firstTemplate.mrt;
      }
    });
    setMrtByTrack(newMrtByTrack);

    // Load response values from templates
    const newResponsesByTrack = { 1: {}, 2: {}, 3: {}, 4: {} };
    [1, 2, 3, 4].forEach(track => {
      const trackTemplates = templatesByTrack[track] || {};
      Object.values(MESSAGE_TYPES).forEach(messageType => {
        const template = trackTemplates[messageType];
        if (template) {
          newResponsesByTrack[track][messageType] = {
            response1: template.response1 || '',
            response2: template.response2 || ''
          };
        }
      });
    });
    setResponsesByTrack(newResponsesByTrack);
  }, [templatesByTrack]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templates = await fetchTemplatesByTrack();
      setTemplatesByTrack(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (messageType, value) => {
    setFormData(prev => ({
      ...prev,
      [messageType]: value
    }));
  };

  const handleMrtChange = (value) => {
    setMrtByTrack(prev => ({
      ...prev,
      [activeTrack]: value
    }));
  };

  const handleResponseChange = (messageType, field, value) => {
    setResponsesByTrack(prev => ({
      ...prev,
      [activeTrack]: {
        ...prev[activeTrack],
        [messageType]: {
          ...prev[activeTrack]?.[messageType],
          [field]: value
        }
      }
    }));
  };

  const handleSaveTrack = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const savedTemplates = await saveTrack(
        activeTrack,
        formData,
        currentUser?.email || 'Unknown',
        templatesByTrack[activeTrack] || {},
        mrtByTrack[activeTrack] || null,
        responsesByTrack[activeTrack] || {}
      );

      // Update local state
      setTemplatesByTrack(prev => ({
        ...prev,
        [activeTrack]: {
          ...prev[activeTrack],
          ...savedTemplates
        }
      }));

      setMessage({ type: 'success', text: `Track ${activeTrack} saved successfully!` });
    } catch (error) {
      console.error('Error saving track:', error);
      setMessage({ type: 'error', text: 'Failed to save track' });
    } finally {
      setSaving(false);
    }
  };

  const getTrackStatus = (trackNumber) => {
    const track = templatesByTrack[trackNumber] || {};
    const hasAll = Object.values(MESSAGE_TYPES).every(type => track[type]?.content);
    const hasAny = Object.values(MESSAGE_TYPES).some(type => track[type]?.content);
    
    if (hasAll) return 'complete';
    if (hasAny) return 'partial';
    return 'empty';
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`bg-gray-900 text-white flex flex-col justify-between transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        <div>
          <div className="p-4 flex items-center justify-between">
            <h1 className="text-xl font-bold whitespace-nowrap overflow-hidden transition-all duration-300">
              {sidebarOpen ? 'Submission Hub' : 'SH'}
            </h1>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
              <Menu size={20} />
            </button>
          </div>
          <nav className="mt-4">
            <ul>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-slots" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Slots</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/manage-appointments" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Appointments</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/" className="flex items-center w-full text-white">
                  <Clock size={20} />
                  {sidebarOpen && <span className="ml-3">Manage Submissions</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
                <Link to="/message-templates" className="flex items-center w-full text-white">
                  <MessageSquare size={20} />
                  {sidebarOpen && <span className="ml-3">Message Templates</span>}
                </Link>
              </li>
              <li className="px-4 py-3 flex items-center bg-gray-800 cursor-pointer">
                <Link to="/appointment-message-variants" className="flex items-center w-full text-white">
                  <MessageSquare size={20} />
                  {sidebarOpen && <span className="ml-3">Message Variants</span>}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        <div className="p-4">
          <div className="flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded" onClick={handleLogout}>
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex items-center justify-end">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
              {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-700">{currentUser?.email || 'User'}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
          <div className="w-full">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Appointment Message Variants</h2>
            <p className="text-gray-600 mb-6">
              Configure 4 message tracks for A/B testing. Each track contains confirmation, 24-hour, and 1-hour reminder messages.
            </p>
            
            {/* Alert Message */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {message.text}
              </div>
            )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading templates...</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border">
              {/* Track Tabs */}
              <div className="flex border-b">
                {[1, 2, 3, 4].map(track => {
                  const status = getTrackStatus(track);
                  return (
                    <button
                      key={track}
                      onClick={() => setActiveTrack(track)}
                      className={`flex-1 px-6 py-4 text-center font-medium transition-colors relative ${
                        activeTrack === track
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>Track {track}</span>
                      <span className={`ml-2 inline-block w-2 h-2 rounded-full ${
                        status === 'complete' ? 'bg-green-500' :
                        status === 'partial' ? 'bg-yellow-500' :
                        'bg-gray-300'
                      }`} />
                    </button>
                  );
                })}
              </div>

              {/* Track Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setActiveTrack(prev => Math.max(1, prev - 1))}
                      disabled={activeTrack === 1}
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h2 className="text-lg font-semibold">Track {activeTrack} Messages</h2>
                    <button
                      onClick={() => setActiveTrack(prev => Math.min(4, prev + 1))}
                      disabled={activeTrack === 4}
                      className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                  <button
                    onClick={handleSaveTrack}
                    disabled={saving}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Saving...' : 'Save Track'}
                  </button>
                </div>

                {/* MRT Dropdown */}
                <div className="mb-6 border rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MRT (Motivational Readiness Theory) Value
                  </label>
                  <select
                    value={mrtByTrack[activeTrack] || ''}
                    onChange={(e) => handleMrtChange(e.target.value)}
                    className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Select MRT Value</option>
                    {Object.entries(MRT_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    Select the MRT value to associate with this track's messages
                  </p>
                </div>

                <div className="space-y-6">
                  {Object.values(MESSAGE_TYPES).map(messageType => (
                    <div key={messageType} className="border rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {MESSAGE_TYPE_LABELS[messageType]}
                      </label>
                      <textarea
                        value={formData[messageType]}
                        onChange={(e) => handleInputChange(messageType, e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Enter ${MESSAGE_TYPE_LABELS[messageType].toLowerCase()} message...`}
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        Available placeholders: {'{{name}}'}, {'{{date}}'}, {'{{time}}'}
                      </div>

                      {/* Custom Response Fields */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Responses</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Response to "1" (Confirm)
                            </label>
                            <textarea
                              value={responsesByTrack[activeTrack]?.[messageType]?.response1 || ''}
                              onChange={(e) => handleResponseChange(messageType, 'response1', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Message sent when user replies 1..."
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Response to "2" (Cancel/Reschedule)
                            </label>
                            <textarea
                              value={responsesByTrack[activeTrack]?.[messageType]?.response2 || ''}
                              onChange={(e) => handleResponseChange(messageType, 'response2', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Message sent when user replies 2..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Track Legend */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Status Legend</h3>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="w-3 h-3 rounded-full bg-green-500 mr-2" />
                      Complete (all 3 messages)
                    </div>
                    <div className="flex items-center">
                      <span className="w-3 h-3 rounded-full bg-yellow-500 mr-2" />
                      Partial (some messages)
                    </div>
                    <div className="flex items-center">
                      <span className="w-3 h-3 rounded-full bg-gray-300 mr-2" />
                      Empty (no messages)
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">How Variant Tracks Work</h3>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc pl-5">
                    <li>When a user books an appointment, they are randomly assigned to one of the 4 tracks</li>
                    <li>The track assignment is tied to their current status</li>
                    <li>If their status changes, they will be assigned a new random track on their next booking</li>
                    <li>All 3 messages (confirmation, 24hr, 1hr) in a track are sent to users assigned to that track</li>
                    <li>Each track can be associated with an MRT (Motivational Readiness Theory) value for research purposes</li>
                    <li>Custom responses can be configured for when users reply with "1" (confirm) or "2" (cancel/reschedule)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppointmentMessageVariantsPage;
